/* ============================================================================
   M4 — Zone resolution engine (ported verbatim from mockup js/m4.js)
   Pure, in-memory, no network. Consumed by ZoneMapper + ZoneOverride.
   ========================================================================== */
import {DUMMY_PINCODES, METROS_DEFAULT, SPECIALS} from '../dummyData'

/* ───────── shared constants ───────── */
export const ZONE_NAMES = {1: 'Local', 2: 'Regional', 3: 'Metro', 4: 'ROI', 5: 'Special'}

export const ZONE_TEMPLATE =
  'pickup_pincode,drop_pincode\n400001,500001\n400001,500032\n110001,560001\n560001,462001\n400001,400051\n'

export const MATCH_FIELDS = [
  {value: 'destCity', label: 'Destination city'},
  {value: 'destState', label: 'Destination state'},
  {value: 'destPin', label: 'Destination pincode'},
  {value: 'pickupCity', label: 'Pickup city'},
  {value: 'pickupState', label: 'Pickup state'},
  {value: 'pickupPin', label: 'Pickup pincode'},
  {value: 'pair', label: 'Pickup-drop pair'},
]
export const MATCH_LABEL = MATCH_FIELDS.reduce((m, f) => {
  m[f.value] = f.label
  return m
}, {})

/* Every pincode in the master (read-only reference data, §M4.2). */
export const ALL_SERVICEABLE = Object.keys(DUMMY_PINCODES)
export const METROS_SEED = METROS_DEFAULT.slice()
export const SPECIALS_SEED = SPECIALS.slice()

/* ───────── master-derived option lists ───────── */
export function pin(p) {
  return DUMMY_PINCODES[String(p).trim()] || null
}
export function pinKnown(p) {
  return !!pin(p)
}
export function uniqueCities() {
  const s = {}
  Object.keys(DUMMY_PINCODES).forEach((p) => (s[DUMMY_PINCODES[p].city] = 1))
  return Object.keys(s).sort()
}
export function uniqueStates() {
  const s = {}
  Object.keys(DUMMY_PINCODES).forEach((p) => (s[DUMMY_PINCODES[p].state] = 1))
  return Object.keys(s).sort()
}
export function specialCities() {
  return Object.keys(DUMMY_PINCODES)
    .filter((p) => DUMMY_PINCODES[p].special)
    .map((p) => DUMMY_PINCODES[p].city)
}
export function datesOverlap(aFrom, aTo, bFrom, bTo) {
  const af = new Date(aFrom).getTime()
  const at = aTo ? new Date(aTo).getTime() : Infinity
  const bf = new Date(bFrom).getTime()
  const bt = bTo ? new Date(bTo).getTime() : Infinity
  return af <= bt && bf <= at
}

/* The M4.3 first-match engine. metros = configured Metro Cities list; specials =
   configured Special-location city names. First match wins. */
export function resolveZone(pickupPin, dropPin, metros, specials) {
  const pu = pin(pickupPin)
  const dr = pin(dropPin)
  if (!pu || !dr) return {zone: null, rule: 'Pincode not found'}
  const isSpecial = !!(dr.special || (specials && specials.indexOf(dr.city) >= 0))
  if (isSpecial) return {zone: 5, rule: 'Step 1 — Special'}
  if (pu.city === dr.city) return {zone: 1, rule: 'Step 2 — Local (same city)'}
  if (pu.state === dr.state) return {zone: 2, rule: 'Step 3 — Regional (same state)'}
  if (metros.indexOf(pu.city) >= 0 && metros.indexOf(dr.city) >= 0)
    return {zone: 3, rule: 'Step 4 — Metro'}
  return {zone: 4, rule: 'Step 5 — ROI fallback'}
}

/* Parse a pickup,drop CSV into [{pickup,drop}] pairs. Splits each line on , ; or
   tab, skips a header line when the first line's first cell is not a 6-digit
   pincode, and keeps only rows where BOTH a pickup and drop are present. */
export function parseZoneCsv(text) {
  let lines = String(text || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
  if (!lines.length) return []
  if (!/^\d{6}/.test(lines[0])) lines = lines.slice(1) // header row
  const out = []
  lines.forEach((ln) => {
    const cells = ln.split(/[,;\t]/).map((c) => c.trim())
    const pickup = cells[0]
    const drop = cells[1]
    if (pickup && drop) out.push({pickup, drop})
  })
  return out
}

/* Resolve the client zone for a courier row given a list of overrides.
   Returns {zone, field} or null (first match wins). */
export function resolveClientZone(courierRow, clientOverrides) {
  for (let i = 0; i < clientOverrides.length; i++) {
    const o = clientOverrides[i]
    const v = String(o.value).toLowerCase()
    const match =
      (o.field === 'destCity' && courierRow.dropCity.toLowerCase() === v) ||
      (o.field === 'destState' && courierRow.dropState.toLowerCase() === v) ||
      (o.field === 'destPin' && courierRow.dropPin === String(o.value)) ||
      (o.field === 'pickupCity' && courierRow.pickupCity.toLowerCase() === v) ||
      (o.field === 'pickupState' && courierRow.pickupState.toLowerCase() === v) ||
      (o.field === 'pickupPin' && courierRow.pickupPin === String(o.value)) ||
      (o.field === 'pair' && courierRow.pickupPin + '-' + courierRow.dropPin === String(o.value))
    if (match) return {zone: o.zone, field: o.field}
  }
  return null
}
